/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, Typography } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';

import selectUser from '../../store/selectors/appSelector';
import theme from '../../theme';
import {
  epochToDateInReadableFormat,
  epochToTimeInReadableFormat,
  getDiffBetweenTwoEpoch,
} from '../../utils';
import BackdropCircularProgress from '../BackdropCircularProgress';
import IconButton from '../IconButton';
import Label from '../Label';
import SwitchComponent from '../SwitchComponent/SwitchComponent';

const headerText = 'Placement Cancelled';

const rosterTypography = theme?.components?.rosterWidget?.typography;

const attendanceTheme = theme?.label?.attendance;

const keyStyle = {
  fontColor: rosterTypography?.cancelledPlacementKey?.fontColor,
  fontSize: rosterTypography?.cancelledPlacementKey?.fontSize,
  fontFamily: rosterTypography?.cancelledPlacementKey?.fontFamily,
};

const valStyle = {
  fontColor: rosterTypography?.cancelledPlacementVal?.fontColor,
  fontSize: rosterTypography?.cancelledPlacementVal?.fontSize,
  fontFamily: rosterTypography?.cancelledPlacementVal?.fontFamily,
};

const RootBox = styled(Box)(() => ({
  height: '100%',
  width: '100%',
  padding: theme.spacing(8, 8, 0),
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
}));

const HeaderBoxTypography = styled(Box)(() => ({
  fontColor: rosterTypography?.cancelledPlacementHeader?.fontColor,
  fontSize: rosterTypography?.cancelledPlacementHeader?.fontSize,
  fontFamily: rosterTypography?.cancelledPlacementHeader?.fontFamily,
}));

const CancelledPlacementDetailDrawer = ({
  cancelledPlacement,
  isOpen,
  onClose,
  handleUpdate,
  isCancelledPlacementUpdating,
}) => {
  const user = useSelector(selectUser);
  const absent = cancelledPlacement?.calcAttendance?.absent?.generatedBySystem;
  const excusedAbsent = cancelledPlacement?.calcAttendance?.absent?.excused?.time;

  const onChange = () => {
    const updateBody = {
      id: cancelledPlacement?._id,
      fields: {
        calcAttendance: {
          absent: !excusedAbsent,
        },
      },
    };
    handleUpdate(updateBody);
  };
  const couldBeExcused = user.role === UserRole.ADMIN && (absent || excusedAbsent);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: '576px',
          height: '100vh',
          padding: '12px',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'end', padding: theme.spacing(2) }}>
        <IconButton
          iconElement={<CloseIcon />}
          onClick={onClose}
          size="small"
          sx={{ marginLeft: 0.5, marginTop: 0.5, position: 'fixed' }}
        />
      </Box>
      <RootBox>
        <Box>
          <HeaderBoxTypography>{`${headerText}`}</HeaderBoxTypography>
          <Grid container sx={{ mt: 2 }}>
            <Typography sx={keyStyle}>{LanguageConverter('jobDetails.cancelledBy')}</Typography>
            <Typography sx={valStyle}> : {cancelledPlacement?.state?.userName}</Typography>
          </Grid>
          {(user.role === UserRole.ADMIN || user.role === UserRole.RECRUITER) && (
            <>
              <Grid container sx={{ mt: 2 }}>
                <Typography sx={keyStyle}>
                  {LanguageConverter('jobDetails.cancelledReason')}
                </Typography>
                <Typography sx={valStyle}>: {cancelledPlacement?.state?.reason || '--'}</Typography>
              </Grid>
              <Grid container sx={{ mt: 2 }}>
                <Typography sx={keyStyle}>{LanguageConverter('jobDetails.cancelNote')} </Typography>
                <Typography sx={valStyle}>
                  : {cancelledPlacement?.state?.cancelNote || '--'}
                </Typography>
              </Grid>
            </>
          )}
          <Grid container sx={{ mt: 2 }}>
            <Typography sx={keyStyle}>{LanguageConverter('jobDetails.timeCancelled')}</Typography>
            <Typography sx={valStyle}>
              : {epochToDateInReadableFormat(cancelledPlacement?.state?.timestamp)} at{' '}
              {epochToTimeInReadableFormat(cancelledPlacement?.state?.timestamp)}
            </Typography>
          </Grid>
          <Grid container sx={{ mt: 2 }}>
            <Typography sx={keyStyle}>{LanguageConverter('jobDetails.timeTillJob')}</Typography>
            <Typography sx={valStyle}>
              :
              {getDiffBetweenTwoEpoch(
                cancelledPlacement?.state?.timestamp,
                cancelledPlacement?.start
              )}
            </Typography>
          </Grid>
          <Grid container direction="column" sx={{ mt: 2 }}>
            <Typography sx={keyStyle}>{LanguageConverter('jobDetails.status')} </Typography>
            {couldBeExcused && (
              <Typography sx={valStyle}>
                {LanguageConverter('attendance.status.subTitle')}
              </Typography>
            )}
          </Grid>

          <Grid container>
            {couldBeExcused && (
              <SwitchComponent initialValue={!!excusedAbsent} handleOnChange={onChange} />
            )}
            {(excusedAbsent && (
              <Label
                sx={{
                  backgroundColor: attendanceTheme?.bgColor?.excusedAbsent,
                  color: attendanceTheme?.color?.excusedAbsent,
                  width: '150px',
                }}
                text={LanguageConverter('attendance.status.excusedAbsent')}
                sxForText={{
                  fontFamily: attendanceTheme?.font,
                }}
              />
            )) ||
              (absent && (
                <Label
                  sx={{
                    backgroundColor: attendanceTheme?.bgColor?.absent,
                    color: attendanceTheme?.color?.absent,
                  }}
                  text={LanguageConverter('attendance.status.absent')}
                  sxForText={{
                    fontFamily: attendanceTheme?.font,
                  }}
                />
              )) || (
                <Label
                  sx={{
                    backgroundColor: attendanceTheme?.bgColor?.cancelled,
                    color: attendanceTheme?.color?.cancelled,
                  }}
                  text={LanguageConverter('attendance.status.cancelled')}
                  sxForText={{
                    fontFamily: attendanceTheme?.font,
                  }}
                />
              )}
          </Grid>
        </Box>
      </RootBox>
      {isCancelledPlacementUpdating && <BackdropCircularProgress />}
    </Drawer>
  );
};

CancelledPlacementDetailDrawer.propTypes = {
  cancelledPlacement: PropTypes.shape({}),
  isOpen: PropTypes.bool,
  isCancelledPlacementUpdating: PropTypes.bool,
  onClose: PropTypes.func,
  handleUpdate: PropTypes.func,
};

export default CancelledPlacementDetailDrawer;
